import * as Y from 'yjs';
import { base64ToUint8Array } from './yjs';
import type { Node, Edge } from '../core/models/scene';
import { ShapeMapAdapter } from '../lib/diagram/ShapeMapAdapter';
import type { DiffHighlight } from '../stores/historyPreviewStore';
import type { ProjectionPresentation } from '../core/canvas/composeProjectedScene';
import { ORG_CUSTOM_FIELD_OVERRIDES_MAP, readProjectedLayout } from '../core/canvas/projectedLayout';

export function extractNodesAndEdgesFromSnapshot(
  snapshotBase64: string,
): ProjectionPresentation {
  const doc = new Y.Doc();
  try {
    Y.applyUpdate(doc, base64ToUint8Array(snapshotBase64));

    return extractNodesAndEdgesFromDoc(doc);
  } finally {
    doc.destroy();
  }
}

export function extractNodesAndEdgesFromDoc(doc: Y.Doc): ProjectionPresentation {
  const projectedLayout = readProjectedLayout(doc);
  const customFieldOverrides = new Map<string, Record<string, string | number>>();
  doc.getMap(ORG_CUSTOM_FIELD_OVERRIDES_MAP).forEach((value, nodeId) => {
    const overrides = value instanceof Y.Map ? value.toJSON() : value;
    if (!overrides || typeof overrides !== 'object') return;
    customFieldOverrides.set(nodeId, { ...(overrides as Record<string, string | number>) });
  });
  // Read the minimal shape format written by the previous implementation so
  // existing documents keep their values while the shape is excluded below.
  new ShapeMapAdapter(doc.getMap('shapes')).forEach((shape) => {
    if (!isLegacyCustomFieldOverrideShape(shape) || customFieldOverrides.has(shape.id)) return;
    const overrides = shape.attrs.customFieldOverrides as Record<string, string | number>;
    customFieldOverrides.set(shape.id, { ...overrides });
  });
  return {
    nodes: extractNodes(doc),
    edges: extractEdges(doc),
    ...(projectedLayout && { projectedLayout }),
    ...(customFieldOverrides.size > 0 && { customFieldOverrides }),
  };
}

function extractNodes(doc: Y.Doc): Node[] {
  const adapter = new ShapeMapAdapter(doc.getMap('shapes'));
  const nodes: Node[] = [];

  adapter.forEach((shape) => {
    // Older versions persisted projected custom values as a minimal shape.
    // It is presentation metadata, not an authored canvas node; ignore it
    // during hydration so it cannot alter projection appearance.
    if (isLegacyCustomFieldOverrideShape(shape)) return;
    const shapeProps = shape.props && typeof shape.props === 'object' ? shape.props : {};
    const shapeAttrs = {
      ...(shape.attrs || {}),
      ...(shapeProps.attrs || {}),
    };

    const deceasedFlag =
      shape.deceased ?? shapeProps.deceased ?? shapeAttrs.isDeceased ?? shapeAttrs.deceased ?? false;

    nodes.push({
      id: shape.id,
      type: shape.type || 'person',
      ...(shapeProps || {}),
      ...(shape.name ? { name: shape.name } : {}),
      ...(shape.sex ? { sex: shape.sex } : {}),
      ...(shape.text ? { text: shape.text } : {}),
      ...(shape.fontSize ? { fontSize: shape.fontSize } : {}),
      ...(shape.fontFamily ? { fontFamily: shape.fontFamily } : {}),
      x: shape.x,
      y: shape.y,
      width: shape.width,
      height: shape.height,
      w: shape.width,
      h: shape.height,
      zIndex: shape.zIndex,
      fillColor: shape.style?.fillColor || shape.fillColor || shapeProps.fillColor,
      strokeColor: shape.style?.strokeColor || shape.strokeColor || shapeProps.strokeColor,
      deceased: Boolean(deceasedFlag),
      locked: Boolean(shape.locked ?? shapeProps.locked ?? shapeAttrs.locked ?? false),
      lockOwner: shape.lockOwner ?? shapeProps.lockOwner,
      attrs: {
        ...(shape.type === 'freehand' ? shapeProps : {}),
        ...shapeAttrs,
        rotation: shape.rotation ?? shapeAttrs.rotation,
      },
    } as any);
  });

  return nodes;
}

function isLegacyCustomFieldOverrideShape(shape: Record<string, any>): boolean {
  const attrs = shape.attrs && typeof shape.attrs === 'object' ? shape.attrs : {};
  if (!attrs.customFieldOverrides || attrs.folderRecordId || attrs.positionIds) return false;
  const keys = Object.keys(shape).filter((key) => shape[key] !== undefined);
  return keys.every((key) => ['id', 'type', 'x', 'y', 'attrs', 'props'].includes(key));
}

function extractEdges(doc: Y.Doc): Edge[] {
  const connectorsMap = doc.getMap<any>('connectors');
  const edges: Edge[] = [];

  connectorsMap.forEach((raw) => {
    const connector = raw instanceof Y.Map ? raw.toJSON() : raw;
    if (!connector || !connector.id) return;

    const props = connector.props;
    const hasNestedAttrs = props && typeof props === 'object' && 'attrs' in props;

    const connectorAttrs = hasNestedAttrs
      ? (props.attrs || {})
      : props &&
          typeof props === 'object' &&
          !('fromAnchor' in props) &&
          !('toAnchor' in props) &&
          !('fromOffset' in props) &&
          !('toOffset' in props)
        ? props
        : (connector.attrs || {});

    const fromId =
      connector.sourceShapeId || connector.source || connector.from;
    const toId =
      connector.targetShapeId || connector.target || connector.to;

    edges.push({
      id: connector.id,
      type: connector.type,
      from: fromId,
      to: toId,
      attrs: connectorAttrs,
      fromAnchor: connector.fromAnchor || props?.fromAnchor,
      toAnchor: connector.toAnchor || props?.toAnchor,
      fromOffset: connector.fromOffset || props?.fromOffset,
      toOffset: connector.toOffset || props?.toOffset,
      fromAnchorLocked: connector.fromAnchorLocked ?? props?.fromAnchorLocked,
      toAnchorLocked: connector.toAnchorLocked ?? props?.toAnchorLocked,
    } as any);
  });

  return edges;
}

const DIFF_IGNORE_KEYS = new Set(['id', 'zIndex']);

function shapeFingerprint(shape: Record<string, any>): string {
  const keys = Object.keys(shape)
    .filter((k) => !DIFF_IGNORE_KEYS.has(k))
    .sort();
  const obj: Record<string, unknown> = {};
  for (const k of keys) {
    obj[k] = shape[k];
  }
  return JSON.stringify(obj);
}

export function computeDiffHighlights(
  snapshotNodes: Node[],
  currentDoc: Y.Doc,
): { highlights: DiffHighlight[]; ghostNodes: Node[] } {
  const highlights: DiffHighlight[] = [];
  const ghostNodes: Node[] = [];

  const snapshotById = new Map(snapshotNodes.map((n) => [n.id, n]));

  const currentAdapter = new ShapeMapAdapter(currentDoc.getMap('shapes'));
  const currentShapes = new Map<string, Record<string, any>>();
  currentAdapter.forEach((shape, key) => {
    currentShapes.set(key, shape);
  });

  // Shapes in snapshot only → deleted since (green)
  for (const node of snapshotNodes) {
    if (!currentShapes.has(node.id)) {
      highlights.push({
        id: node.id,
        x: node.x,
        y: node.y,
        width: (node.w ?? node.width ?? 60) as number,
        height: (node.h ?? node.height ?? 60) as number,
        type: 'deleted',
      });
    }
  }

  // Shapes in both → check for modifications (yellow)
  for (const node of snapshotNodes) {
    const current = currentShapes.get(node.id);
    if (!current) continue;

    // Compare key visual properties from snapshot node vs current shape
    const snapshotComparable: Record<string, any> = {
      x: node.x,
      y: node.y,
      width: node.width,
      height: node.height,
      type: node.type,
      name: (node as any).name,
      sex: (node as any).sex,
      fillColor: (node as any).fillColor,
      strokeColor: (node as any).strokeColor,
      deceased: (node as any).deceased,
    };
    const currentComparable: Record<string, any> = {
      x: current.x,
      y: current.y,
      width: current.width,
      height: current.height,
      type: current.type,
      name: current.name,
      sex: current.sex,
      fillColor: current.style?.fillColor || current.fillColor || current.props?.fillColor,
      strokeColor: current.style?.strokeColor || current.strokeColor || current.props?.strokeColor,
      deceased: Boolean(current.deceased ?? current.props?.deceased ?? false),
    };

    if (shapeFingerprint(snapshotComparable) !== shapeFingerprint(currentComparable)) {
      highlights.push({
        id: node.id,
        x: node.x,
        y: node.y,
        width: (node.w ?? node.width ?? 60) as number,
        height: (node.h ?? node.height ?? 60) as number,
        type: 'modified',
      });
    }
  }

  // Shapes in current only → added since (red) + ghost nodes
  for (const [id, shape] of currentShapes) {
    if (snapshotById.has(id)) continue;

    const shapeProps = shape.props && typeof shape.props === 'object' ? shape.props : {};
    const shapeAttrs = {
      ...(shape.attrs || {}),
      ...(shapeProps.attrs || {}),
    };
    const deceasedFlag =
      shape.deceased ?? shapeProps.deceased ?? shapeAttrs.isDeceased ?? shapeAttrs.deceased ?? false;

    const w = (shape.width ?? 60) as number;
    const h = (shape.height ?? 60) as number;

    highlights.push({
      id,
      x: shape.x,
      y: shape.y,
      width: w,
      height: h,
      type: 'added',
    });

    ghostNodes.push({
      id,
      type: shape.type || 'person',
      ...(shapeProps || {}),
      ...(shape.name ? { name: shape.name } : {}),
      ...(shape.sex ? { sex: shape.sex } : {}),
      ...(shape.text ? { text: shape.text } : {}),
      ...(shape.fontSize ? { fontSize: shape.fontSize } : {}),
      ...(shape.fontFamily ? { fontFamily: shape.fontFamily } : {}),
      x: shape.x,
      y: shape.y,
      width: w,
      height: h,
      w,
      h,
      zIndex: shape.zIndex,
      fillColor: shape.style?.fillColor || shape.fillColor || shapeProps.fillColor,
      strokeColor: shape.style?.strokeColor || shape.strokeColor || shapeProps.strokeColor,
      deceased: Boolean(deceasedFlag),
      locked: Boolean(shape.locked ?? shapeProps.locked ?? shapeAttrs.locked ?? false),
      attrs: {
        ...shapeAttrs,
        rotation: shape.rotation ?? shapeAttrs.rotation,
      },
      _ghostOpacity: 0.3,
    } as any);
  }

  return { highlights, ghostNodes };
}
